import type { Nullable, NullableProps } from '@/types/utils';

type ImageAsset = {
  src: string;
};

export const IMAGE_ASSETS_PATH = '/assets/img/';

/**
 * @description if path contains "asset/" or img/ cut those and everything before them
 * so it will be relative to assets folder
 */
const getUnifiedPath = (path: string, prefixesToRemove = ['assets/', 'img/']): string => {
  const unifiedPath = prefixesToRemove.reduce((currentPath, prefix) => {
    return currentPath.includes(prefix) ? currentPath.split(prefix)[1] : currentPath;
  }, path);

  return unifiedPath.startsWith('/') ? unifiedPath.slice(1) : unifiedPath;
};

const glob = import.meta.glob(`@/assets/img/**/*`, { eager: true });
const images = Object.fromEntries(
  Object.entries(glob).map(([key, value]: any) => {
    return [key, value.default];
  }),
);

/**
 * @example const { src: logoSrc } = useImageAssets('logos/logo.png');
 * @example const images = useImageAssets(['logo.png', 'logo2.png']);
 * @example const { src: logoSrc } = useImageAssets('img/logo.png');
 * @example const { src: logoSrc } = useImageAssets('@/assets/img/logo.png');
 * @example const { src: logoSrc } = useImageAssets('img/logos/logo.png');
 */
export function useImageAssets(path: string): Nullable<NullableProps<ImageAsset>>;
export function useImageAssets(path: Array<string>): Array<Nullable<NullableProps<ImageAsset>>>;

export function useImageAssets(path: any): any {
  if (Array.isArray(path)) {
    return path.map((p) => {
      if (!p || typeof p !== 'string') {
        return null;
      }

      const unifiedPath = getUnifiedPath(p);
      const result = images[`${IMAGE_ASSETS_PATH}${unifiedPath}`] || null;

      return { src: result };
    });
  }

  if (!path || typeof path !== 'string') {
    return null;
  }

  return { src: images[`${IMAGE_ASSETS_PATH}${getUnifiedPath(path)}`] || null };
}
